/**
 * Session guard — one place that handles an expired session for the whole pane.
 *
 * Two surfaces touch an authenticated endpoint, and both funnel here on a 401:
 *  - the Files view load on open (declarative HTMX — the configRequest hook
 *    below attaches the bearer, and responseError catches its 401), and
 *  - the chunked-upload fetches (which call check401() on each response).
 *
 * On a 401 the stored session_oid is dead and the staged uploads belonged to
 * its now-gone draft, so we clear the connection record and this draft's staged
 * queue, then send the user back to the connect page. A full navigation is
 * deliberate: it drops all in-memory pane state and re-runs connect from a
 * clean slate.
 */

import { getSessionId, clearConnection } from "./roaming_settings.js";
import { clearQueue } from "./staged_queue.js";

/** Thrown by check401() so upload callers can stop without rendering an error. */
export class SessionExpiredError extends Error {
  constructor() {
    super("Session expired");
    this.name = "SessionExpiredError";
  }
}

// Guard so concurrent 401s (e.g. several in-flight chunk uploads) trigger the
// clear-and-redirect exactly once.
let handling = false;

/**
 * Drop all pane state (connection record + this draft's staged queue) and
 * navigate to the connect page. The shared clean-slate teardown for both an
 * expired session (below) and an explicit sign-out (files_fragment.js): a full
 * navigation re-runs connect from scratch, dropping any in-memory pane state.
 */
export async function resetToConnect() {
  await clearConnection();
  await clearQueue();
  window.location.assign(window.location.origin + "/outlook/connect");
}

/** Clear roaming state and return to the connect page. Runs at most once. */
async function handleSessionExpired() {
  if (handling) return;
  handling = true;
  await resetToConnect();
}

/** If a fetch Response is a 401, start the expiry flow and throw to stop the caller. */
export function check401(resp) {
  if (resp && resp.status === 401) {
    handleSessionExpired();
    throw new SessionExpiredError();
  }
}

/** Wire the HTMX bearer header + 401 handling. Call once at startup. */
export function initSessionGuard() {
  // Attach the pane's access token to HTMX requests (the Files view load) — the
  // same Bearer shape the upload fetches use. Only when a session is stored.
  document.body.addEventListener("htmx:configRequest", (evt) => {
    const session = getSessionId();
    if (session) evt.detail.headers["Authorization"] = `Bearer ${session}`;
  });

  // A 401 from any HTMX request means the stored session is gone. Intercept at
  // beforeSwap (fires before the swap) so the global "Something went wrong"
  // error fragment never renders: cancel the swap and run the expiry flow,
  // which clears roaming state and reloads to the connect page.
  document.body.addEventListener("htmx:beforeSwap", (evt) => {
    const xhr = evt.detail && evt.detail.xhr;
    if (xhr && xhr.status === 401) {
      evt.detail.shouldSwap = false;
      evt.detail.isError = false;
      handleSessionExpired();
    }
  });
}
